export function escapeHtml(s: string): string {
    return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

/**
 * Inverse of {@link escapeHtml}: turns HTML entities back into their literal characters.
 *
 * Use when a value is persisted HTML-escaped (e.g. a template/custom-field name stored as
 * `DPIA - GDPR &amp; AI`) but needs to be displayed as plain text via interpolation `{{ }}`,
 * which does NOT decode entities. `&amp;` is decoded last so values like `&amp;lt;` don't
 * collapse into a real `&lt;`.
 *
 * NOT a general-purpose HTML-entity decoder — only call it on values known to have been
 * escaped via {@link escapeHtml}. It handles the five entities escapeHtml emits
 * (`&amp; &lt; &gt; &quot;`) plus two it does not: `&#39;` (apostrophe, emitted by the
 * attribute-escaping variants in the codebase) and `&nbsp;` (defensive — some backend
 * paths persist non-breaking spaces). Other entities (`&apos;`, numeric `&#…;`, etc.)
 * pass through un-decoded and would render raw.
 */
export function decodeHtml(s: string): string {
    return s
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&quot;/g, '"')
        .replace(/&#39;/g, "'")
        .replace(/&nbsp;/g, ' ')
        .replace(/&amp;/g, '&');
}

/**
 * {@link decodeHtml} applied across the shapes a Mira/vendor-scout suggested answer
 * arrives in: a plain string, or an array of strings (chips / multi-select). Non-string
 * members (and non-string values) are returned untouched, so it's safe to call on a
 * mixed/unknown answer before applying it to a form control. Use this at the *approve*
 * step so the parsed value is what gets persisted — not the raw HTML-escaped wire value.
 */
export function decodeHtmlDeep<T>(value: T): T {
    if (typeof value === 'string') return decodeHtml(value) as unknown as T;
    if (Array.isArray(value)) return value.map(v => (typeof v === 'string' ? decodeHtml(v) : v)) as unknown as T;
    return value;
}

const NAMED_HTML_ENTITIES: Record<string, string> = {
    amp: '&',
    lt: '<',
    gt: '>',
    quot: '"',
    apos: "'",
    nbsp: ' ',
};

/**
 * General HTML-entity decoder: turns named (`&amp; &lt; &gt; &quot; &apos; &nbsp;`) and
 * numeric (`&#39;`, `&#x27;`) entities back into their literal characters.
 *
 * Single-pass regex: the scanner never re-reads its own replacements, so `&amp;lt;` decodes
 * to `&lt;` (NOT `<`) and the function is safe to run on already-decoded or mixed content.
 * Superset of {@link decodeHtml}. Used by the HTML-decode HTTP interceptor and safe for
 * plain-text display via `{{ }}` (Angular still escapes the decoded text for the DOM).
 */
export function decodeHtmlEntities(value: string | null | undefined): string {
    if (!value) {
        return value ?? '';
    }
    return value.replace(/&(amp|lt|gt|quot|apos|nbsp|#\d+|#x[0-9a-fA-F]+);/g, (match, entity: string) => {
        if (entity[0] === '#') {
            const codePoint = entity[1] === 'x' || entity[1] === 'X'
                ? parseInt(entity.slice(2), 16)
                : parseInt(entity.slice(1), 10);
            try {
                return String.fromCodePoint(codePoint);
            } catch {
                return match;
            }
        }
        return NAMED_HTML_ENTITIES[entity] ?? match;
    });
}
